import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StudentSelectionGrid } from './StudentSelectionGrid';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { toast } from 'sonner';
import { useMessagesTranslations } from './hooks/useMessagesTranslations';
import { Channel } from './types';

type CreateChannelDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChannelCreated: (channel: Channel) => void;
};

type Student = {
  auth_id: string;
  first_name: string;
  last_name: string;
  avatar_url?: string | null;
};

export const CreateChannelDialog = ({ open, onOpenChange, onChannelCreated }: CreateChannelDialogProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const { user } = useAuth();
  const { t } = useMessagesTranslations();

  useEffect(() => {
    if (!user || !open) {
      setStudentsLoading(true);
      return;
    }
    console.log('[CreateChannelDialog] useEffect triggered for fetching students.');
    const fetchStudents = async () => {
      setStudentsLoading(true);
      try {
        console.log('[CreateChannelDialog] Fetching students for trainer_id:', user.id);
        const { data: studentsData, error: studentsError } = await supabase
          .from('students')
          .select('auth_id, first_name, last_name, avatar_url')
          .eq('trainer_id', user.id)
          .eq('status', 'active');
        console.log('[CreateChannelDialog] Supabase students data:', studentsData, 'error:', studentsError);
        if (studentsError) throw studentsError;
        
        const eligibleStudents = studentsData?.filter(s => s.auth_id) || [];
        console.log('[CreateChannelDialog] Eligible students for selection:', eligibleStudents);
        setStudents(eligibleStudents);
      } catch (error) {
        console.error('Error fetching students:', error);
        toast.error(t('errorLoadingContacts') || 'Erro ao carregar contactos.');
      } finally {
        setStudentsLoading(false);
      }
    };

    setName('');
    setDescription('');
    setSelectedStudents([]);
    fetchStudents();
  }, [user?.id, open]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[CreateChannelDialog] Name input changed:', e.target.value);
    setName(e.target.value);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    console.log('[CreateChannelDialog] Description input changed:', e.target.value);
    setDescription(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    if (!name.trim()) {
      toast.error(t('channelNameRequired'));
      return;
    }
    
    try {
      setLoading(true);
      
      const { data: channelData, error: channelError } = await supabase
        .from('message_channels')
        .insert({
          name: name.trim(),
          description: description.trim() || null,
          trainer_id: user.id
        })
        .select('*')
        .single();
      
      if (channelError) throw channelError;

      const members = [
        { channel_id: channelData.id, user_id: user.id, role: 'owner' },
        ...selectedStudents.map(studentId => ({
          channel_id: channelData.id,
          user_id: studentId,
          role: 'member'
        }))
      ];

      const { error: membersError } = await supabase
        .from('channel_members')
        .insert(members);
      
      if (membersError) throw membersError;
      
      toast.success(t('channelCreated'));
      onChannelCreated(channelData);
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating channel:', error);
      toast.error(t('errorCreatingChannel'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectStudent = (studentId: string) => {
    console.log('[CreateChannelDialog] Selecting student:', studentId);
    setSelectedStudents(prev => [...prev, studentId]);
  };

  const handleRemoveStudent = (studentId: string) => {
    console.log('[CreateChannelDialog] Removing student:', studentId);
    setSelectedStudents(prev => prev.filter(id => id !== studentId));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('createChannel')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="name">{t('channelName')}</Label>
            <Input
              id="name"
              value={name}
              onChange={handleNameChange}
              placeholder={t('channelNamePlaceholder')}
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="description">{t('channelDescription')}</Label>
            <Textarea
              id="description"
              value={description}
              onChange={handleDescriptionChange}
              placeholder={t('channelDescriptionPlaceholder')}
              rows={3}
            />
          </div>
          
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              {t('cancel')}
            </Button>
            <Button 
              type="submit" 
              disabled={loading || !name.trim() || studentsLoading}
            >
              {loading ? t('creating') : t('create')}
            </Button>
          </DialogFooter>
        </form>

        <div className="grid gap-2 mt-4 pt-4 border-t dark:border-gray-700">
          <Label>{t('participants')}</Label>
          <StudentSelectionGrid
            students={students}
            selectedStudents={selectedStudents}
            onSelectStudent={handleSelectStudent}
            onRemoveStudent={handleRemoveStudent}
            loading={studentsLoading}
          />
        </div>

      </DialogContent>
    </Dialog>
  );
};
